import { useSchedulerStore, type ExecutionLog } from '@/stores/schedulerStore.js';
import { Badge } from '@/components/ui/badge.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import {
    X,
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Loader2,
    ExternalLink,
    Calendar,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { formatDistanceToNow, format } from 'date-fns';

interface ScheduleDetailOverlayProps {
    scheduleId: string;
    onClose: () => void;
}

function getStatusIcon(status: ExecutionLog['status']) {
    switch (status) {
        case 'success':
            return <CheckCircle2 className="h-4 w-4 text-green-500" />;
        case 'failed':
            return <XCircle className="h-4 w-4 text-red-500" />;
        case 'timeout':
            return <AlertTriangle className="h-4 w-4 text-amber-500" />;
        case 'pending':
        default:
            return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
    }
}

function formatDuration(ms?: number): string {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

export function ScheduleDetailOverlay({ scheduleId, onClose }: ScheduleDetailOverlayProps) {
    const navigate = useNavigate();
    const { setCurrentSession } = useSessionStore();
    const { getSchedule, getScheduleExecutionLogs, runningScheduleIds } = useSchedulerStore();

    const schedule = getSchedule(scheduleId);
    const executionLogs = getScheduleExecutionLogs(scheduleId);
    const isRunning = runningScheduleIds.has(scheduleId);

    const handleViewSession = (sessionId: string) => {
        setCurrentSession(sessionId);
        navigate(`/chat/${sessionId}`);
        onClose();
    };

    if (!schedule) {
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="text-center">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Routine not found</p>
                    <Button variant="outline" onClick={onClose} className="mt-4">
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    const hasError = !!schedule.lastError;

    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex">
            {/* Overlay click to close */}
            <div className="absolute inset-0" onClick={onClose} />

            {/* Panel */}
            <div className="relative ml-auto w-full max-w-lg bg-background border-l shadow-xl flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div className="flex items-center gap-3">
                        <div
                            className={cn(
                                'flex-shrink-0',
                                isRunning && 'text-yellow-500',
                                hasError && !isRunning && 'text-red-500',
                                !schedule.enabled && !isRunning && 'text-muted-foreground',
                                schedule.enabled && !hasError && !isRunning && 'text-green-500'
                            )}
                        >
                            {isRunning ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : hasError ? (
                                <AlertTriangle className="h-5 w-5" />
                            ) : !schedule.enabled ? (
                                <AlertTriangle className="h-5 w-5" />
                            ) : (
                                <CheckCircle2 className="h-5 w-5" />
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="font-semibold">{schedule.name}</h2>
                                {isRunning && (
                                    <Badge variant="secondary" className="text-[10px] h-5 px-2 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400 animate-pulse">
                                        Running
                                    </Badge>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">{schedule.cronExpression}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                <ScrollArea className="flex-1">
                    <div className="p-4 space-y-6">
                        {/* Details */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-medium">Task Details</h3>
                            <div className="space-y-2">
                                <div>
                                    <div className="text-xs text-muted-foreground mb-1">Instruction</div>
                                    <div className="p-3 rounded-lg bg-muted/50 text-sm">
                                        {schedule.task.instruction}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="p-3 rounded-lg border text-center">
                                <div className="text-2xl font-semibold">{schedule.runCount}</div>
                                <div className="text-xs text-muted-foreground">Total Runs</div>
                            </div>
                            <div className="p-3 rounded-lg border text-center">
                                <div className="text-2xl font-semibold text-green-600">
                                    {schedule.successCount}
                                </div>
                                <div className="text-xs text-muted-foreground">Successful</div>
                            </div>
                            <div className="p-3 rounded-lg border text-center">
                                <div className="text-2xl font-semibold text-red-600">
                                    {schedule.failureCount}
                                </div>
                                <div className="text-xs text-muted-foreground">Failed</div>
                            </div>
                        </div>

                        {/* Schedule Info */}
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Timezone</span>
                                <span>{schedule.timezone}</span>
                            </div>
                            {schedule.nextRunAt && schedule.enabled && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Next Run</span>
                                    <span>
                                        {format(new Date(schedule.nextRunAt), 'MMM d, h:mm a')}
                                    </span>
                                </div>
                            )}
                            {schedule.lastRunAt && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Last Run</span>
                                    <span>
                                        {formatDistanceToNow(new Date(schedule.lastRunAt), {
                                            addSuffix: true,
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Error */}
                        {hasError && (
                            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
                                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-medium mb-1">
                                    <XCircle className="h-4 w-4" />
                                    Last Error
                                </div>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    {schedule.lastError}
                                </p>
                            </div>
                        )}

                        {/* Execution History */}
                        <div className="space-y-3">
                            <h3 className="text-sm font-medium">Execution History</h3>

                            {executionLogs.length === 0 ? (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                    No executions yet
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {executionLogs.map((log) => (
                                        <div
                                            key={log.id}
                                            className={cn(
                                                'p-3 rounded-lg border transition-colors',
                                                log.sessionId && 'cursor-pointer hover:bg-accent/50'
                                            )}
                                            onClick={() =>
                                                log.sessionId && handleViewSession(log.sessionId)
                                            }
                                        >
                                            <div className="flex items-center gap-2">
                                                {getStatusIcon(log.status)}
                                                <span className="text-sm font-medium capitalize">
                                                    {log.status}
                                                </span>
                                                <div className="flex-1" />
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDistanceToNow(
                                                        new Date(log.triggeredAt),
                                                        {
                                                            addSuffix: true,
                                                        }
                                                    )}
                                                </span>
                                                {log.sessionId && (
                                                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground pl-6">
                                                <span>Duration: {formatDuration(log.duration)}</span>
                                                <span>
                                                    {format(
                                                        new Date(log.triggeredAt),
                                                        'MMM d, h:mm:ss a'
                                                    )}
                                                </span>
                                            </div>
                                            {log.error && (
                                                <div className="mt-2 pl-6 text-xs text-red-600 dark:text-red-400">
                                                    {log.error}
                                                </div>
                                            )}
                                            {log.result && (
                                                <div className="mt-2 pl-6 text-xs text-muted-foreground line-clamp-2">
                                                    {log.result}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}
